import dayjs, { setDayjsLocale, withActiveLocale, type Dayjs } from '@atlas/tool/box/date/dayjs';

/**
 *   A wrapper class for handling date and time operations in the Atlas application.
 * This class provides convenient methods for parsing, formatting, and displaying dates
 * with specific timezone handling.
 *
 * @class
 * @example
 * ```typescript
 * // Create from UTC string
 * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
 *
 * // Format the date
 * console.log(dateTime.fullDateTime); // "Dec 25, 2023, 10:30 AM"
 *
 * // Get relative time
 * console.log(dateTime.relative); // "2 hours ago"
 * ```
 */
class AtlasDateTime {
    private _date: Dayjs;

    constructor(d: Dayjs) {
        this._date = d;
    }

    static fromUTCString(utcString: string) {
        const dayjsUtc = dayjs(typeof utcString === 'string' && utcString.endsWith('Z') ? utcString : utcString + 'Z');

        return new AtlasDateTime(dayjsUtc);
    }

    /**
     * Parts of a "<date> between <start> and <end>" label.
     *
     * Returns the pieces rather than a sentence: "between ... and ..." is copy,
     * and copy belongs in a message catalog so it follows the reader's language.
     * Callers format it, e.g. `intl.formatMessage(messages.sessionDateRange, parts)`.
     */
    static getDateRange(start: Dayjs, end: Dayjs): { date: string; start: string; end: string } {
        const atlasStart = new AtlasDateTime(start);

        return {
            date: atlasStart.shortDate,
            start: withActiveLocale(start).format('LT'),
            end: withActiveLocale(end).format('LT'),
        };
    }

    private format(format: DateFormat) {
        const formatBody = dateFormats[format];
        const now = dayjs();
        const isToday = this._date.isSame(now, 'day');
        const date = withActiveLocale(this._date);

        if (isToday) {
            return date.format(formatBody.current);
        }

        const isThisYear = this._date.isSame(now, 'year');

        if (isThisYear) {
            return date.format(formatBody.priorDay || formatBody.current);
        }

        return date.format(formatBody.priorYear || formatBody.current);
    }

    /**
     * Returns the date in the short date format.
     * @returns {string} The date in the format "MMM D" or "MMM D, YYYY" if the date is not in the current year.
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.shortDate); // "Dec 25, 2023"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.shortDate); // "Dec 25"
     * ```
     */
    get shortDate() {
        return this.format(DateFormat.SHORT_DATE);
    }

    /**
     * Returns the date in the short date time format.
     * @returns {string} The date in the format "h:mm A", "MMM D, h:mm A", or "MMM D, YYYY, h:mm A" depending on prior year or day.
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.shortDateTime); // "10:30 AM"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.shortDateTime); // "Dec 25, 10:30 AM"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.shortDateTime); // "Dec 25, 2023, 10:30 AM"
     * ```
     */
    get shortDateTime() {
        return this.format(DateFormat.SHORT_DATE_TIME);
    }

    /**
     * Returns the date in the full date time format.
     * @returns {string} The date in the format "MMM D, YYYY, h:mm A".
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.fullDateTime); // "Dec 25, 2023, 10:30 AM"
     * ```
     */
    get fullDateTime() {
        return this.format(DateFormat.FULL_DATE_TIME);
    }

    /**
     * Returns the date in the tiny date time format.
     * @returns {string} The date in the format "h:mm A", "MMM D", or "MMM D, YYYY" depending on prior year or day.
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.tinyDateTime); // "10:30 AM"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.tinyDateTime); // "Dec 25"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.tinyDateTime); // "Dec 25, 2023"
     * ```
     */
    get tinyDateTime() {
        return this.format(DateFormat.TINY_DATE_TIME);
    }

    /**
     * Returns the relative time from the current date.
     *
     * Delegates to dayjs's `fromNow`, which reads the active locale, so this is
     * "2 hours ago" in English, "vor 2 Stunden" in German and so on. Building the
     * string by hand here meant every locale got English months ago.
     *
     * @returns {string} The relative time, e.g. "a few seconds ago" or "2 days ago".
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.relative); // "2 hours ago"
     * ```
     * @example
     * ```typescript
     * const dateTime = AtlasDateTime.fromUTCString('2023-12-25T10:30:00');
     * console.log(dateTime.relative); // "2 days ago"
     * ```
     */
    get relative() {
        return withActiveLocale(this._date).fromNow();
    }
}

export default AtlasDateTime;

// #region Types

export enum DateFormat {
    SHORT_DATE = 'shortDate',
    SHORT_DATE_TIME = 'shortDateTime',
    FULL_DATE_TIME = 'fullDateTime',
    TINY_DATE_TIME = 'tinyDateTime',
}

type DateFormatBody = {
    current: string;
    priorDay?: string;
    priorYear?: string;
};

// #endregion

// #region Helpers

// Tokens are dayjs's *localized* ones (`ll`, `LT`) wherever an equivalent
// exists, so the layout follows the locale and not just the month names: `LT` is
// "10:30 AM" in English and "10:30" in German, `ll` is "Dec 25, 2023" and
// "25. Dez. 2023". The bare `MMM D` entries have no localized equivalent without
// a year (dayjs has no "month day, no year" token), so they keep the English
// ordering with a translated month name.
const dateFormats: Record<DateFormat, DateFormatBody> = {
    [DateFormat.SHORT_DATE]: {
        current: 'MMM D',
        priorYear: 'll',
    },
    [DateFormat.SHORT_DATE_TIME]: {
        current: 'LT',
        priorDay: 'MMM D, LT',
        priorYear: 'll, LT',
    },
    [DateFormat.FULL_DATE_TIME]: {
        current: 'll, LT',
    },
    [DateFormat.TINY_DATE_TIME]: {
        current: 'LT',
        priorDay: 'MMM D',
        priorYear: 'll',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect, afterEach, beforeEach } = import.meta.vitest;

    describe('AtlasDateTime', () => {
        // A fixed local instant away from a year boundary, so the day/year
        // branching is deterministic without depending on the runner's timezone.
        const christmas2023 = () => new AtlasDateTime(dayjs('2023-12-25T10:30:00'));

        beforeEach(() => setDayjsLocale('en'));
        afterEach(() => setDayjsLocale('en'));

        it('formats a past year in the reader language', () => {
            expect(christmas2023().fullDateTime).toBe('Dec 25, 2023, 10:30 AM');

            setDayjsLocale('de');
            expect(christmas2023().fullDateTime).toBe('25. Dez. 2023, 10:30');
        });

        it('drops the year for dates in the current year', () => {
            const lastMonth = dayjs().subtract(1, 'month').hour(10).minute(30);

            expect(new AtlasDateTime(lastMonth).shortDateTime).toBe(`${lastMonth.format('MMM D')}, 10:30 AM`);
        });

        it('shows only the time for today', () => {
            const today = dayjs().hour(10).minute(30);

            setDayjsLocale('de');
            expect(new AtlasDateTime(today).tinyDateTime).toBe('10:30');

            setDayjsLocale('en');
            expect(new AtlasDateTime(today).tinyDateTime).toBe('10:30 AM');
        });

        it('describes elapsed time in the reader language', () => {
            const threeHoursAgo = dayjs().subtract(3, 'hour');

            expect(new AtlasDateTime(threeHoursAgo).relative).toBe('3 hours ago');

            setDayjsLocale('es');
            expect(new AtlasDateTime(threeHoursAgo).relative).toBe('hace 3 horas');
        });

        it('returns the parts of a date range, not an English sentence', () => {
            const start = dayjs('2024-05-06T18:30:00');
            const end = dayjs('2024-05-06T19:45:00');

            setDayjsLocale('de');

            expect(AtlasDateTime.getDateRange(start, end)).toEqual({
                date: '6. Mai 2024',
                start: '18:30',
                end: '19:45',
            });
        });
    });
}

// #endregion
